import type { Language } from "../../types/i18n";

/*
 * Part of the appPages module (see ./appPages.ts), split out so one file stays
 * editable. English is the reference; the other languages must carry the same
 * keys, which the record type enforces.
 *
 * Strings for the sign-in, password reset, email verification and
 * registration pages in src/pages/auth/.
 */
const en = {
  // Shared across the auth pages
  auth2LogoAlt: "HealthPass logo",
  auth2Imprint: "Imprint",
  auth2EmailPlaceholder: "your-email@example.com",
  auth2PersonalEmailPlaceholder: "john@example.com",
  auth2FirstNamePlaceholder: "John",
  auth2LastNamePlaceholder: "Smith",
  auth2PhonePlaceholder: "(555) 123-4567",
  auth2AddressPlaceholder: "123 Main St, Springfield, IL 62701",
  auth2ShowPassword: "Show password",
  auth2HidePassword: "Hide password",
  auth2PasswordMinPlaceholder: "At least 8 characters",
  auth2RepeatPasswordPlaceholder: "Repeat password",
  auth2PasswordsDoNotMatch: "Passwords do not match",
  auth2Registering: "Registering…",
  auth2AlreadyHaveAccountSignIn: "Already have an account? Sign in",
  auth2AlreadyRegisteredSignIn: "Already registered? Sign in",
  auth2GoToSignIn: "Go to sign in",
  auth2Website: "Website",
  auth2BusinessLicense: "Business license",
  auth2CompanyInformation: "Company information",
  auth2CompanyNameRequired: "Company name *",
  auth2HealthcareProvider: "Healthcare Provider",
  auth2TourOperator: "Tour Operator",
  auth2AffiliatePartner: "Affiliate Partner",
  auth2BackToProviderSelection: "← Back to provider selection",
  auth2CopyrightLine: "© {year} HealthPass. All rights reserved.",

  // Login
  auth2SecondStepFailed:
    "We could not start the second step. Please try again shortly.",
  auth2PasskeyFailed: "That passkey could not be used to sign in.",
  auth2SigningIn: "Signing in…",
  auth2Or: "or",
  auth2WaitingForPasskey: "Waiting for your passkey…",
  auth2SignInWithPasskey: "Sign in with a passkey",

  // Forgot password
  auth2ResetEmailError: "We could not send the email. Please try again.",
  auth2ResetLinkSentTo: "We sent a password reset link to",
  auth2NoEmailReceived: "No email? Check your spam folder.",
  auth2ResendEmail: "Resend email",
  auth2ResetLinkInfo:
    "You will receive an email with a secure link to reset your password.",

  // Reset password
  auth2ResetExpired:
    "That link has expired. Reset links last an hour and work once - request a new one.",
  auth2ResetInvalid:
    "That link is not valid. It may have been used already, or your mail client cut the address short.",
  auth2ResetUnavailable:
    "Something on our side is temporarily unavailable. Your link is still good - try again in a few minutes.",
  auth2PasswordsNotSame: "Those two passwords are not the same.",
  auth2ChooseStrongerPassword: "Please choose a stronger password.",
  auth2ChooseNewPassword: "Choose a new password",
  auth2PasswordChanged: "Password changed",
  auth2SignInWithNewPassword: "You can sign in with your new password now.",
  auth2CouldNotReset: "We could not reset it",
  auth2RequestNewLink: "Request a new link",
  auth2PasswordRules:
    "At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol.",
  auth2RepeatNewPassword: "Repeat it",
  auth2SetNewPassword: "Set new password",
  auth2NeedsEmailLink: "This page needs the link from your email.",

  // Verify email
  auth2ConfirmYourEmail: "Confirm your email",
  auth2ConfirmingEmail: "Confirming your email",
  auth2OneMoment: "One moment.",
  auth2EmailConfirmedTitle: "Email confirmed",
  auth2EmailConfirmedBody:
    "Your address is verified. You can sign in and use HealthPass.",
  auth2VerifyExpiredTitle: "That link has expired",
  auth2VerifyExpiredBody:
    "Verification links last 24 hours and work once. Sign in and request a new one from your account settings.",
  auth2VerifyInvalidTitle: "That link is not valid",
  auth2VerifyInvalidBody:
    "It may have been used already, or the address was cut short by your mail client. Try opening it again from the original message.",
  auth2VerifyUnavailableTitle: "We could not confirm it just now",
  auth2VerifyUnavailableBody:
    "Something on our side is temporarily unavailable. Your link is still good - please try again in a few minutes.",

  // Provider type selection
  auth2ProviderRegistration: "Provider registration",
  auth2ProviderRegistrationSubtitle: "Choose your provider type to register",
  auth2HealthcareProviderDesc:
    "Clinics, medical practices, vaccination centers and other medical facilities",
  auth2TagVaccinationCertificates: "Vaccination certificates",
  auth2TagTravelMedicine: "Travel medicine",
  auth2TagHealthRecords: "Health records",
  auth2TourOperatorDesc: "Tour operators, travel agencies and tourism partners",
  auth2TagTripPlanning: "Trip planning",
  auth2TagCustomerSupport: "Customer support",
  auth2TagHealthRequirements: "Health requirements",
  auth2AffiliatePartnerDesc:
    "Insurers, technology partners and service providers",
  auth2TagPartnerships: "Partnerships",
  auth2TagServices: "Services",
  auth2PrivateUserQuestion: "Signing up for yourself?",
  auth2StandardRegistration: "Create a personal account",

  // Tour operator admin
  auth2TourOperatorAdminTitle: "Tour Operator Administrator",
  auth2TourOperatorAdminSubtitle: "Register as a tour operator administrator",
  auth2TravelEmailPlaceholder: "john@travelagency.com",
  auth2TravelCompanyPlaceholder: "Sample Travel Inc.",
  auth2OperatorType: "Operator type",
  auth2OpTravelAgency: "Travel agency",
  auth2OpCruiseLine: "Cruise line",
  auth2OpAirline: "Airline",
  auth2OpHotelChain: "Hotel chain",
  auth2OpCarRental: "Car rental",
  auth2OpAdventureTours: "Adventure tours",
  auth2OpCulturalTours: "Cultural tours",
  auth2OpBusinessTravel: "Business travel",
  auth2TourOperatorPermissions: "Tour operator administrator permissions:",
  auth2TourPermTrips: "Manage trips and bookings",
  auth2TourPermHealthRequirements:
    "Access health requirements for destinations",
  auth2TourPermStaff: "Invite and manage staff",
  auth2TourPermSupport: "Customer support",
  auth2TourPermReports: "View reports and statistics",
  auth2RegisterTourOperatorAdmin: "Register as tour operator administrator",

  // Healthcare provider admin
  auth2CreateAdminAccount: "Create an administrator account",
  auth2OrganizationRequired: "Organization / institution *",
  auth2OrganizationPlaceholder: "e.g. City Medical Center",
  auth2ProviderAdminCan: "As a healthcare provider administrator you can:",
  auth2ProviderPermHealthData: "View and manage health data",
  auth2ProviderPermInviteStaff: "Invite additional provider staff",
  auth2ProviderPermCertificates: "Issue vaccination certificates",
  auth2ProviderPermConsultations: "Offer travel medicine consultations",
  auth2RegisterAsRegularUser: "Register as a regular user",

  // Healthcare provider (facility) registration
  auth2HcpRegistrationTitle: "Healthcare provider registration",
  auth2HcpRegistrationSubtitle:
    "Register your healthcare facility on HealthPass",
  auth2FacilityInformation: "Facility information",
  auth2FacilityNameRequired: "Facility name *",
  auth2FacilityTypeRequired: "Facility type *",
  auth2FacilityHospital: "Hospital",
  auth2FacilityClinic: "Clinic",
  auth2FacilityPharmacy: "Pharmacy",
  auth2FacilityLaboratory: "Laboratory",
  auth2FacilityDoctorOffice: "Doctor's office",
  auth2FacilityNursingHome: "Nursing home",
  auth2FacilityRehabCenter: "Rehabilitation center",
  auth2FacilityMentalHealth: "Mental health center",
  auth2FacilityEmergencyServices: "Emergency services",
  auth2FacilityEmailRequired: "Facility email *",
  auth2LicenseNumber: "License / registration number",
  auth2StreetAddressRequired: "Street address *",
  auth2ServicesAndSpecialties: "Services and specialties",
  auth2ServicesCommaSeparated: "Services (comma-separated)",
  auth2ServicesPlaceholder: "e.g. General medicine, Surgery, Radiology",
  auth2SpecialtiesCommaSeparated: "Specialties (comma-separated)",
  auth2SpecialtiesPlaceholder: "e.g. Cardiology, Neurology, Pediatrics",
  auth2AdministratorAccount: "Administrator account",

  // Affiliate partner admin
  auth2AffiliateRegistered: "Affiliate partner admin registered successfully!",
  auth2AffiliatePartnerAdmin: "Affiliate Partner Admin",
  auth2AffiliateSubtitle: "Registration for affiliate partner administrators",
  auth2PartnerEmailPlaceholder: "admin@partnercompany.com",
  auth2InsuranceCompanyPlaceholder: "Sample Insurance Inc.",
  auth2WebsitePlaceholder: "https://www.partnercompany.com",
  auth2CompanyDescriptionPlaceholder:
    "Brief description of your company and the services you offer…",
  auth2PartnerType: "Partner type",
  auth2PartnerTravelInsurance: "Travel insurance",
  auth2PartnerHealthInsurance: "Health insurance",
  auth2PartnerMedicalEquipment: "Medical equipment",
  auth2PartnerPharmaceutical: "Pharmaceutical",
  auth2PartnerWellness: "Wellness services",
  auth2PartnerTelemedicine: "Telemedicine",
  auth2PartnerDiagnostics: "Diagnostic services",
  auth2PartnerEmergencyAssistance: "Emergency assistance",
  auth2PartnerLogistics: "Logistics provider",
  auth2PartnerFintech: "FinTech",
  auth2PartnershipType: "Partnership type",
  auth2PartnershipReferral: "Referral",
  auth2PartnershipCommission: "Commission-based",
  auth2PartnershipRevenueShare: "Revenue share",
  auth2PartnershipWhiteLabel: "White label",
  auth2PartnershipReseller: "Reseller",
  auth2PartnershipTechnology: "Technology partner",
  auth2PartnershipContent: "Content partner",
  auth2RegisterAffiliateAdmin: "Register as affiliate partner admin",
  auth2AffiliateRoleHeading: "Your role as affiliate partner admin:",
  auth2AffiliateRoleServices: "Manage partner services and products",
  auth2AffiliateRoleAcquisition:
    "Customer acquisition and partnership management",
  auth2AffiliateRoleAnalytics: "Analytics and performance tracking",
  auth2AffiliateRoleBilling: "Billing and commissions",
  auth2AffiliateRoleIntegration: "Integration into the HealthPass ecosystem",
  auth2OtherProviderType: "Different provider type?",
  auth2ProviderSelection: "Choose provider type",

  // Traveler registration wizard
  auth2PlanFree: "Free plan",
  auth2PlanStandard: "Standard plan",
  auth2PlanPremium: "Premium plan",
  auth2PlanNoTimeLimit: "No time limit",
  auth2PlanMonthlyOrOneTime: "per month (12 months) or {oneTime} one-time",
  auth2FeatPersonalProfile: "Personal profile",
  auth2FeatStandardQuestionnaire: "Standard medical history questionnaire",
  auth2FeatQrSharing: "Data sharing by QR code",
  auth2FeatStorage6Months: "Data stored for 6 months",
  auth2FeatEmergencyContacts: "Emergency contacts",
  auth2FeatAllFree: "Everything in Free",
  auth2FeatMedMessage: "MedMessage: smart notifications",
  auth2FeatVaccinationsMedPlan: "Vaccination certificates & medication plan",
  auth2FeatTravelReadiness: "Travel Readiness Check",
  auth2FeatPartnerOffers: "Partner travel offers",
  auth2FeatConsentManagement: "Data consent management",
  auth2FeatStorage100Mb: "Up to 100 MB storage",
  auth2FeatAllStandard: "Everything in Standard",
  auth2FeatTinaAssistant: "Tina, the AI travel health assistant",
  auth2FeatCareProfile: "Care profile and nutrition profile",
  auth2FeatHealthMonitoring: "Health monitoring while traveling",
  auth2FeatInviteProvider: "Invite a healthcare provider",
  auth2FeatPersonalizedOffers: "Personalized offers",
  auth2WelcomeToHealthPass: "Welcome to HealthPass",
  auth2WelcomeSubtitle: "Your health, always with you - secure and digital",
  auth2DateOfBirthOptional: "Date of birth (optional)",
  auth2PhoneOptional: "Phone number (optional)",
  auth2SecureAccessSubtitle: "Secure access to your health data",
  auth2PasswordMin8Label: "Password * (at least 8 characters)",
  auth2ChooseYourPlan: "Choose your plan",
  auth2ChooseYourPlanSubtitle: "A good fit for every kind of traveler",
  auth2Popular: "Popular",
  auth2MoreFeatures: "+ {count} more features",
  auth2AlmostDone: "Almost done!",
  auth2ConfirmRegistration: "Confirm your registration",
  auth2YourSelection: "Your selection",
  auth2Plan: "Plan",
  auth2IAcceptThe: "I accept the",
  auth2And: "and",
  auth2NewsletterOptIn:
    "I want to receive the newsletter with travel health tips (optional)",
  auth2CompleteRegistration: "Complete registration",
  auth2SignInHere: "Sign in here",
};

export type AppPagesAuthStrings = typeof en;

const de: AppPagesAuthStrings = {
  auth2LogoAlt: "HealthPass-Logo",
  auth2Imprint: "Impressum",
  auth2EmailPlaceholder: "ihre-email@beispiel.de",
  auth2PersonalEmailPlaceholder: "max@beispiel.de",
  auth2FirstNamePlaceholder: "Max",
  auth2LastNamePlaceholder: "Mustermann",
  auth2PhonePlaceholder: "+49 30 12345678",
  auth2AddressPlaceholder: "Musterstraße 123, 12345 Musterstadt",
  auth2ShowPassword: "Passwort anzeigen",
  auth2HidePassword: "Passwort verbergen",
  auth2PasswordMinPlaceholder: "Mindestens 8 Zeichen",
  auth2RepeatPasswordPlaceholder: "Passwort wiederholen",
  auth2PasswordsDoNotMatch: "Die Passwörter stimmen nicht überein",
  auth2Registering: "Wird registriert …",
  auth2AlreadyHaveAccountSignIn: "Bereits ein Konto? Anmelden",
  auth2AlreadyRegisteredSignIn: "Bereits registriert? Anmelden",
  auth2GoToSignIn: "Zur Anmeldung",
  auth2Website: "Website",
  auth2BusinessLicense: "Gewerbeerlaubnis",
  auth2CompanyInformation: "Unternehmensangaben",
  auth2CompanyNameRequired: "Name des Unternehmens *",
  auth2HealthcareProvider: "Gesundheitsdienstleister",
  auth2TourOperator: "Reiseveranstalter",
  auth2AffiliatePartner: "Affiliate-Partner",
  auth2BackToProviderSelection: "← Zurück zur Anbieterauswahl",
  auth2CopyrightLine: "© {year} HealthPass. Alle Rechte vorbehalten.",

  auth2SecondStepFailed:
    "Der zweite Schritt konnte nicht gestartet werden. Bitte versuchen Sie es in Kürze erneut.",
  auth2PasskeyFailed: "Mit diesem Passkey war keine Anmeldung möglich.",
  auth2SigningIn: "Anmeldung läuft …",
  auth2Or: "oder",
  auth2WaitingForPasskey: "Warte auf Ihren Passkey …",
  auth2SignInWithPasskey: "Mit Passkey anmelden",

  auth2ResetEmailError:
    "Die E-Mail konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
  auth2ResetLinkSentTo:
    "Wir haben einen Link zum Zurücksetzen des Passworts gesendet an",
  auth2NoEmailReceived: "Keine E-Mail erhalten? Prüfen Sie Ihren Spam-Ordner.",
  auth2ResendEmail: "E-Mail erneut senden",
  auth2ResetLinkInfo:
    "Sie erhalten eine E-Mail mit einem sicheren Link, über den Sie Ihr Passwort zurücksetzen können.",

  auth2ResetExpired:
    "Dieser Link ist abgelaufen. Links zum Zurücksetzen gelten eine Stunde und nur einmal – fordern Sie einen neuen an.",
  auth2ResetInvalid:
    "Dieser Link ist ungültig. Möglicherweise wurde er bereits verwendet oder Ihr E-Mail-Programm hat die Adresse abgeschnitten.",
  auth2ResetUnavailable:
    "Bei uns ist vorübergehend etwas nicht erreichbar. Ihr Link bleibt gültig – versuchen Sie es in ein paar Minuten erneut.",
  auth2PasswordsNotSame: "Die beiden Passwörter sind nicht identisch.",
  auth2ChooseStrongerPassword: "Bitte wählen Sie ein stärkeres Passwort.",
  auth2ChooseNewPassword: "Neues Passwort festlegen",
  auth2PasswordChanged: "Passwort geändert",
  auth2SignInWithNewPassword:
    "Sie können sich jetzt mit Ihrem neuen Passwort anmelden.",
  auth2CouldNotReset: "Das Zurücksetzen hat nicht geklappt",
  auth2RequestNewLink: "Neuen Link anfordern",
  auth2PasswordRules:
    "Mindestens 8 Zeichen, mit einem Groß- und einem Kleinbuchstaben, einer Zahl und einem Sonderzeichen.",
  auth2RepeatNewPassword: "Wiederholen",
  auth2SetNewPassword: "Neues Passwort speichern",
  auth2NeedsEmailLink: "Diese Seite benötigt den Link aus Ihrer E-Mail.",

  auth2ConfirmYourEmail: "E-Mail-Adresse bestätigen",
  auth2ConfirmingEmail: "Ihre E-Mail-Adresse wird bestätigt",
  auth2OneMoment: "Einen Moment bitte.",
  auth2EmailConfirmedTitle: "E-Mail-Adresse bestätigt",
  auth2EmailConfirmedBody:
    "Ihre Adresse ist bestätigt. Sie können sich anmelden und HealthPass nutzen.",
  auth2VerifyExpiredTitle: "Dieser Link ist abgelaufen",
  auth2VerifyExpiredBody:
    "Bestätigungslinks gelten 24 Stunden und nur einmal. Melden Sie sich an und fordern Sie in Ihren Kontoeinstellungen einen neuen an.",
  auth2VerifyInvalidTitle: "Dieser Link ist ungültig",
  auth2VerifyInvalidBody:
    "Möglicherweise wurde er bereits verwendet oder Ihr E-Mail-Programm hat die Adresse abgeschnitten. Öffnen Sie ihn erneut aus der ursprünglichen Nachricht.",
  auth2VerifyUnavailableTitle: "Die Bestätigung ist gerade nicht möglich",
  auth2VerifyUnavailableBody:
    "Bei uns ist vorübergehend etwas nicht erreichbar. Ihr Link bleibt gültig – bitte versuchen Sie es in ein paar Minuten erneut.",

  auth2ProviderRegistration: "Registrierung für Anbieter",
  auth2ProviderRegistrationSubtitle:
    "Wählen Sie Ihre Anbieterart für die Registrierung",
  auth2HealthcareProviderDesc:
    "Kliniken, Arztpraxen, Impfzentren und andere medizinische Einrichtungen",
  auth2TagVaccinationCertificates: "Impfnachweise",
  auth2TagTravelMedicine: "Reisemedizin",
  auth2TagHealthRecords: "Gesundheitsakten",
  auth2TourOperatorDesc: "Reiseveranstalter, Reisebüros und Tourismuspartner",
  auth2TagTripPlanning: "Reiseplanung",
  auth2TagCustomerSupport: "Kundenbetreuung",
  auth2TagHealthRequirements: "Gesundheitsanforderungen",
  auth2AffiliatePartnerDesc:
    "Versicherer, Technologiepartner und Dienstleister",
  auth2TagPartnerships: "Partnerschaften",
  auth2TagServices: "Dienstleistungen",
  auth2PrivateUserQuestion: "Sie registrieren sich privat?",
  auth2StandardRegistration: "Persönliches Konto erstellen",

  auth2TourOperatorAdminTitle: "Administrator für Reiseveranstalter",
  auth2TourOperatorAdminSubtitle:
    "Als Administrator eines Reiseveranstalters registrieren",
  auth2TravelEmailPlaceholder: "max@reiseagentur.de",
  auth2TravelCompanyPlaceholder: "Beispiel Reisen GmbH",
  auth2OperatorType: "Art des Veranstalters",
  auth2OpTravelAgency: "Reisebüro",
  auth2OpCruiseLine: "Kreuzfahrtreederei",
  auth2OpAirline: "Fluggesellschaft",
  auth2OpHotelChain: "Hotelkette",
  auth2OpCarRental: "Autovermietung",
  auth2OpAdventureTours: "Abenteuerreisen",
  auth2OpCulturalTours: "Kulturreisen",
  auth2OpBusinessTravel: "Geschäftsreisen",
  auth2TourOperatorPermissions:
    "Berechtigungen als Administrator eines Reiseveranstalters:",
  auth2TourPermTrips: "Reisen und Buchungen verwalten",
  auth2TourPermHealthRequirements:
    "Gesundheitsanforderungen für Reiseziele einsehen",
  auth2TourPermStaff: "Mitarbeitende einladen und verwalten",
  auth2TourPermSupport: "Kundenbetreuung",
  auth2TourPermReports: "Berichte und Statistiken einsehen",
  auth2RegisterTourOperatorAdmin:
    "Als Administrator eines Reiseveranstalters registrieren",

  auth2CreateAdminAccount: "Administratorkonto erstellen",
  auth2OrganizationRequired: "Organisation / Einrichtung *",
  auth2OrganizationPlaceholder: "z. B. Medizinisches Zentrum Musterstadt",
  auth2ProviderAdminCan:
    "Als Administrator eines Gesundheitsdienstleisters können Sie:",
  auth2ProviderPermHealthData: "Gesundheitsdaten einsehen und verwalten",
  auth2ProviderPermInviteStaff: "Weitere Mitarbeitende einladen",
  auth2ProviderPermCertificates: "Impfnachweise ausstellen",
  auth2ProviderPermConsultations: "Reisemedizinische Beratungen anbieten",
  auth2RegisterAsRegularUser: "Als normaler Nutzer registrieren",

  auth2HcpRegistrationTitle: "Registrierung für Gesundheitsdienstleister",
  auth2HcpRegistrationSubtitle:
    "Registrieren Sie Ihre Gesundheitseinrichtung bei HealthPass",
  auth2FacilityInformation: "Angaben zur Einrichtung",
  auth2FacilityNameRequired: "Name der Einrichtung *",
  auth2FacilityTypeRequired: "Art der Einrichtung *",
  auth2FacilityHospital: "Krankenhaus",
  auth2FacilityClinic: "Klinik",
  auth2FacilityPharmacy: "Apotheke",
  auth2FacilityLaboratory: "Labor",
  auth2FacilityDoctorOffice: "Arztpraxis",
  auth2FacilityNursingHome: "Pflegeheim",
  auth2FacilityRehabCenter: "Rehabilitationszentrum",
  auth2FacilityMentalHealth: "Psychiatrische Einrichtung",
  auth2FacilityEmergencyServices: "Notfalldienst",
  auth2FacilityEmailRequired: "E-Mail-Adresse der Einrichtung *",
  auth2LicenseNumber: "Lizenz- / Zulassungsnummer",
  auth2StreetAddressRequired: "Straße und Hausnummer *",
  auth2ServicesAndSpecialties: "Leistungen und Fachgebiete",
  auth2ServicesCommaSeparated: "Leistungen (durch Kommas getrennt)",
  auth2ServicesPlaceholder: "z. B. Allgemeinmedizin, Chirurgie, Radiologie",
  auth2SpecialtiesCommaSeparated: "Fachgebiete (durch Kommas getrennt)",
  auth2SpecialtiesPlaceholder: "z. B. Kardiologie, Neurologie, Pädiatrie",
  auth2AdministratorAccount: "Administratorkonto",

  auth2AffiliateRegistered:
    "Administrator des Affiliate-Partners erfolgreich registriert!",
  auth2AffiliatePartnerAdmin: "Administrator für Affiliate-Partner",
  auth2AffiliateSubtitle:
    "Registrierung für Administratoren von Affiliate-Partnern",
  auth2PartnerEmailPlaceholder: "admin@partnerunternehmen.de",
  auth2InsuranceCompanyPlaceholder: "Musterversicherung GmbH",
  auth2WebsitePlaceholder: "https://www.partnerunternehmen.de",
  auth2CompanyDescriptionPlaceholder:
    "Kurze Beschreibung Ihres Unternehmens und Ihrer Leistungen …",
  auth2PartnerType: "Partnerart",
  auth2PartnerTravelInsurance: "Reiseversicherung",
  auth2PartnerHealthInsurance: "Krankenversicherung",
  auth2PartnerMedicalEquipment: "Medizinische Ausrüstung",
  auth2PartnerPharmaceutical: "Pharmazeutik",
  auth2PartnerWellness: "Wellness-Dienstleistungen",
  auth2PartnerTelemedicine: "Telemedizin",
  auth2PartnerDiagnostics: "Diagnostische Leistungen",
  auth2PartnerEmergencyAssistance: "Notfallhilfe",
  auth2PartnerLogistics: "Logistikdienstleister",
  auth2PartnerFintech: "FinTech",
  auth2PartnershipType: "Art der Partnerschaft",
  auth2PartnershipReferral: "Empfehlung",
  auth2PartnershipCommission: "Provisionsbasiert",
  auth2PartnershipRevenueShare: "Umsatzbeteiligung",
  auth2PartnershipWhiteLabel: "White Label",
  auth2PartnershipReseller: "Wiederverkäufer",
  auth2PartnershipTechnology: "Technologiepartner",
  auth2PartnershipContent: "Content-Partner",
  auth2RegisterAffiliateAdmin:
    "Als Administrator eines Affiliate-Partners registrieren",
  auth2AffiliateRoleHeading:
    "Ihre Rolle als Administrator eines Affiliate-Partners:",
  auth2AffiliateRoleServices: "Partnerleistungen und Produkte verwalten",
  auth2AffiliateRoleAcquisition: "Kundengewinnung und Partnerschaftsmanagement",
  auth2AffiliateRoleAnalytics: "Analysen und Leistungsauswertung",
  auth2AffiliateRoleBilling: "Abrechnung und Provisionen",
  auth2AffiliateRoleIntegration: "Integration in das HealthPass-Ökosystem",
  auth2OtherProviderType: "Andere Anbieterart?",
  auth2ProviderSelection: "Anbieterart wählen",

  auth2PlanFree: "Free-Tarif",
  auth2PlanStandard: "Standard-Tarif",
  auth2PlanPremium: "Premium-Tarif",
  auth2PlanNoTimeLimit: "Zeitlich unbegrenzt",
  auth2PlanMonthlyOrOneTime: "pro Monat (12 Monate) oder einmalig {oneTime}",
  auth2FeatPersonalProfile: "Persönliches Profil",
  auth2FeatStandardQuestionnaire: "Standard-Anamnesefragebogen",
  auth2FeatQrSharing: "Datenfreigabe per QR-Code",
  auth2FeatStorage6Months: "Datenspeicherung für 6 Monate",
  auth2FeatEmergencyContacts: "Notfallkontakte",
  auth2FeatAllFree: "Alle Free-Funktionen",
  auth2FeatMedMessage: "MedMessage: intelligente Benachrichtigungen",
  auth2FeatVaccinationsMedPlan: "Impfnachweise & Medikationsplan",
  auth2FeatTravelReadiness: "Reisebereitschafts-Check",
  auth2FeatPartnerOffers: "Reiseangebote von Partnern",
  auth2FeatConsentManagement: "Verwaltung von Datenfreigaben",
  auth2FeatStorage100Mb: "Bis zu 100 MB Speicher",
  auth2FeatAllStandard: "Alle Standard-Funktionen",
  auth2FeatTinaAssistant: "Tina, die KI-Assistentin für Reisegesundheit",
  auth2FeatCareProfile: "Pflegeprofil und Ernährungsprofil",
  auth2FeatHealthMonitoring: "Gesundheitsüberwachung auf Reisen",
  auth2FeatInviteProvider: "Gesundheitsdienstleister einladen",
  auth2FeatPersonalizedOffers: "Personalisierte Angebote",
  auth2WelcomeToHealthPass: "Willkommen bei HealthPass",
  auth2WelcomeSubtitle: "Ihre Gesundheit, immer dabei – sicher und digital",
  auth2DateOfBirthOptional: "Geburtsdatum (optional)",
  auth2PhoneOptional: "Telefonnummer (optional)",
  auth2SecureAccessSubtitle: "Sicherer Zugang zu Ihren Gesundheitsdaten",
  auth2PasswordMin8Label: "Passwort * (mindestens 8 Zeichen)",
  auth2ChooseYourPlan: "Wählen Sie Ihren Tarif",
  auth2ChooseYourPlanSubtitle: "Passend für jede Art zu reisen",
  auth2Popular: "Beliebt",
  auth2MoreFeatures: "+ {count} weitere Funktionen",
  auth2AlmostDone: "Fast geschafft!",
  auth2ConfirmRegistration: "Bestätigen Sie Ihre Registrierung",
  auth2YourSelection: "Ihre Auswahl",
  auth2Plan: "Tarif",
  auth2IAcceptThe: "Ich akzeptiere die",
  auth2And: "und die",
  auth2NewsletterOptIn:
    "Ich möchte den Newsletter mit Tipps zur Reisegesundheit erhalten (optional)",
  auth2CompleteRegistration: "Registrierung abschließen",
  auth2SignInHere: "Hier anmelden",
};

const fr: AppPagesAuthStrings = {
  auth2LogoAlt: "Logo HealthPass",
  auth2Imprint: "Mentions légales",
  auth2EmailPlaceholder: "votre-email@exemple.fr",
  auth2PersonalEmailPlaceholder: "jean@exemple.fr",
  auth2FirstNamePlaceholder: "Jean",
  auth2LastNamePlaceholder: "Dupont",
  auth2PhonePlaceholder: "+33 1 23 45 67 89",
  auth2AddressPlaceholder: "12 rue de la Paix, 75002 Paris",
  auth2ShowPassword: "Afficher le mot de passe",
  auth2HidePassword: "Masquer le mot de passe",
  auth2PasswordMinPlaceholder: "Au moins 8 caractères",
  auth2RepeatPasswordPlaceholder: "Répétez le mot de passe",
  auth2PasswordsDoNotMatch: "Les mots de passe ne correspondent pas",
  auth2Registering: "Inscription en cours…",
  auth2AlreadyHaveAccountSignIn: "Vous avez déjà un compte ? Connectez-vous",
  auth2AlreadyRegisteredSignIn: "Déjà inscrit ? Connectez-vous",
  auth2GoToSignIn: "Aller à la connexion",
  auth2Website: "Site web",
  auth2BusinessLicense: "Licence commerciale",
  auth2CompanyInformation: "Informations sur l'entreprise",
  auth2CompanyNameRequired: "Nom de l'entreprise *",
  auth2HealthcareProvider: "Prestataire de santé",
  auth2TourOperator: "Voyagiste",
  auth2AffiliatePartner: "Partenaire affilié",
  auth2BackToProviderSelection: "← Retour au choix du prestataire",
  auth2CopyrightLine: "© {year} HealthPass. Tous droits réservés.",

  auth2SecondStepFailed:
    "Impossible de lancer la deuxième étape. Veuillez réessayer dans un instant.",
  auth2PasskeyFailed:
    "Cette clé d'accès n'a pas pu être utilisée pour vous connecter.",
  auth2SigningIn: "Connexion en cours…",
  auth2Or: "ou",
  auth2WaitingForPasskey: "En attente de votre clé d'accès…",
  auth2SignInWithPasskey: "Se connecter avec une clé d'accès",

  auth2ResetEmailError: "Impossible d'envoyer l'e-mail. Veuillez réessayer.",
  auth2ResetLinkSentTo:
    "Nous avons envoyé un lien de réinitialisation du mot de passe à",
  auth2NoEmailReceived:
    "Pas d'e-mail ? Vérifiez votre dossier de courrier indésirable.",
  auth2ResendEmail: "Renvoyer l'e-mail",
  auth2ResetLinkInfo:
    "Vous recevrez un e-mail contenant un lien sécurisé pour réinitialiser votre mot de passe.",

  auth2ResetExpired:
    "Ce lien a expiré. Les liens de réinitialisation sont valables une heure et une seule fois – demandez-en un nouveau.",
  auth2ResetInvalid:
    "Ce lien n'est pas valide. Il a peut-être déjà été utilisé, ou votre messagerie a tronqué l'adresse.",
  auth2ResetUnavailable:
    "Un service est temporairement indisponible de notre côté. Votre lien reste valable – réessayez dans quelques minutes.",
  auth2PasswordsNotSame: "Les deux mots de passe ne sont pas identiques.",
  auth2ChooseStrongerPassword: "Veuillez choisir un mot de passe plus robuste.",
  auth2ChooseNewPassword: "Choisissez un nouveau mot de passe",
  auth2PasswordChanged: "Mot de passe modifié",
  auth2SignInWithNewPassword:
    "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
  auth2CouldNotReset: "La réinitialisation a échoué",
  auth2RequestNewLink: "Demander un nouveau lien",
  auth2PasswordRules:
    "Au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un symbole.",
  auth2RepeatNewPassword: "Confirmez-le",
  auth2SetNewPassword: "Enregistrer le nouveau mot de passe",
  auth2NeedsEmailLink: "Cette page nécessite le lien reçu par e-mail.",

  auth2ConfirmYourEmail: "Confirmez votre adresse e-mail",
  auth2ConfirmingEmail: "Confirmation de votre adresse e-mail",
  auth2OneMoment: "Un instant.",
  auth2EmailConfirmedTitle: "Adresse e-mail confirmée",
  auth2EmailConfirmedBody:
    "Votre adresse est vérifiée. Vous pouvez vous connecter et utiliser HealthPass.",
  auth2VerifyExpiredTitle: "Ce lien a expiré",
  auth2VerifyExpiredBody:
    "Les liens de vérification sont valables 24 heures et une seule fois. Connectez-vous et demandez-en un nouveau dans les paramètres de votre compte.",
  auth2VerifyInvalidTitle: "Ce lien n'est pas valide",
  auth2VerifyInvalidBody:
    "Il a peut-être déjà été utilisé, ou votre messagerie a tronqué l'adresse. Essayez de l'ouvrir à nouveau depuis le message d'origine.",
  auth2VerifyUnavailableTitle: "Confirmation impossible pour le moment",
  auth2VerifyUnavailableBody:
    "Un service est temporairement indisponible de notre côté. Votre lien reste valable – veuillez réessayer dans quelques minutes.",

  auth2ProviderRegistration: "Inscription des prestataires",
  auth2ProviderRegistrationSubtitle:
    "Choisissez votre type de prestataire pour vous inscrire",
  auth2HealthcareProviderDesc:
    "Cliniques, cabinets médicaux, centres de vaccination et autres établissements médicaux",
  auth2TagVaccinationCertificates: "Certificats de vaccination",
  auth2TagTravelMedicine: "Médecine des voyages",
  auth2TagHealthRecords: "Dossiers de santé",
  auth2TourOperatorDesc:
    "Voyagistes, agences de voyage et partenaires touristiques",
  auth2TagTripPlanning: "Planification de voyages",
  auth2TagCustomerSupport: "Service client",
  auth2TagHealthRequirements: "Exigences sanitaires",
  auth2AffiliatePartnerDesc:
    "Assureurs, partenaires technologiques et prestataires de services",
  auth2TagPartnerships: "Partenariats",
  auth2TagServices: "Services",
  auth2PrivateUserQuestion: "Vous vous inscrivez à titre personnel ?",
  auth2StandardRegistration: "Créer un compte personnel",

  auth2TourOperatorAdminTitle: "Administrateur voyagiste",
  auth2TourOperatorAdminSubtitle:
    "Inscrivez-vous en tant qu'administrateur voyagiste",
  auth2TravelEmailPlaceholder: "jean@agencedevoyage.fr",
  auth2TravelCompanyPlaceholder: "Voyages Exemple SARL",
  auth2OperatorType: "Type d'opérateur",
  auth2OpTravelAgency: "Agence de voyage",
  auth2OpCruiseLine: "Compagnie de croisière",
  auth2OpAirline: "Compagnie aérienne",
  auth2OpHotelChain: "Chaîne hôtelière",
  auth2OpCarRental: "Location de voitures",
  auth2OpAdventureTours: "Voyages d'aventure",
  auth2OpCulturalTours: "Voyages culturels",
  auth2OpBusinessTravel: "Voyages d'affaires",
  auth2TourOperatorPermissions: "Droits de l'administrateur voyagiste :",
  auth2TourPermTrips: "Gérer les voyages et les réservations",
  auth2TourPermHealthRequirements:
    "Consulter les exigences sanitaires des destinations",
  auth2TourPermStaff: "Inviter et gérer le personnel",
  auth2TourPermSupport: "Service client",
  auth2TourPermReports: "Consulter les rapports et statistiques",
  auth2RegisterTourOperatorAdmin:
    "S'inscrire en tant qu'administrateur voyagiste",

  auth2CreateAdminAccount: "Créer un compte administrateur",
  auth2OrganizationRequired: "Organisation / établissement *",
  auth2OrganizationPlaceholder: "p. ex. Centre médical de la ville",
  auth2ProviderAdminCan:
    "En tant qu'administrateur d'un prestataire de santé, vous pouvez :",
  auth2ProviderPermHealthData: "Consulter et gérer les données de santé",
  auth2ProviderPermInviteStaff: "Inviter d'autres membres du personnel",
  auth2ProviderPermCertificates: "Délivrer des certificats de vaccination",
  auth2ProviderPermConsultations:
    "Proposer des consultations de médecine des voyages",
  auth2RegisterAsRegularUser: "S'inscrire en tant qu'utilisateur standard",

  auth2HcpRegistrationTitle: "Inscription des prestataires de santé",
  auth2HcpRegistrationSubtitle:
    "Inscrivez votre établissement de santé sur HealthPass",
  auth2FacilityInformation: "Informations sur l'établissement",
  auth2FacilityNameRequired: "Nom de l'établissement *",
  auth2FacilityTypeRequired: "Type d'établissement *",
  auth2FacilityHospital: "Hôpital",
  auth2FacilityClinic: "Clinique",
  auth2FacilityPharmacy: "Pharmacie",
  auth2FacilityLaboratory: "Laboratoire",
  auth2FacilityDoctorOffice: "Cabinet médical",
  auth2FacilityNursingHome: "Maison de retraite médicalisée",
  auth2FacilityRehabCenter: "Centre de rééducation",
  auth2FacilityMentalHealth: "Établissement de santé mentale",
  auth2FacilityEmergencyServices: "Services d'urgence",
  auth2FacilityEmailRequired: "E-mail de l'établissement *",
  auth2LicenseNumber: "Numéro de licence / d'agrément",
  auth2StreetAddressRequired: "Adresse (rue et numéro) *",
  auth2ServicesAndSpecialties: "Services et spécialités",
  auth2ServicesCommaSeparated: "Services (séparés par des virgules)",
  auth2ServicesPlaceholder: "p. ex. Médecine générale, Chirurgie, Radiologie",
  auth2SpecialtiesCommaSeparated: "Spécialités (séparées par des virgules)",
  auth2SpecialtiesPlaceholder: "p. ex. Cardiologie, Neurologie, Pédiatrie",
  auth2AdministratorAccount: "Compte administrateur",

  auth2AffiliateRegistered:
    "Administrateur du partenaire affilié inscrit avec succès !",
  auth2AffiliatePartnerAdmin: "Administrateur partenaire affilié",
  auth2AffiliateSubtitle:
    "Inscription des administrateurs de partenaires affiliés",
  auth2PartnerEmailPlaceholder: "admin@entreprisepartenaire.fr",
  auth2InsuranceCompanyPlaceholder: "Assurances Exemple SA",
  auth2WebsitePlaceholder: "https://www.entreprisepartenaire.fr",
  auth2CompanyDescriptionPlaceholder:
    "Brève description de votre entreprise et des services proposés…",
  auth2PartnerType: "Type de partenaire",
  auth2PartnerTravelInsurance: "Assurance voyage",
  auth2PartnerHealthInsurance: "Assurance santé",
  auth2PartnerMedicalEquipment: "Équipement médical",
  auth2PartnerPharmaceutical: "Pharmaceutique",
  auth2PartnerWellness: "Services de bien-être",
  auth2PartnerTelemedicine: "Télémédecine",
  auth2PartnerDiagnostics: "Services de diagnostic",
  auth2PartnerEmergencyAssistance: "Assistance d'urgence",
  auth2PartnerLogistics: "Prestataire logistique",
  auth2PartnerFintech: "FinTech",
  auth2PartnershipType: "Type de partenariat",
  auth2PartnershipReferral: "Recommandation",
  auth2PartnershipCommission: "À la commission",
  auth2PartnershipRevenueShare: "Partage des revenus",
  auth2PartnershipWhiteLabel: "Marque blanche",
  auth2PartnershipReseller: "Revendeur",
  auth2PartnershipTechnology: "Partenaire technologique",
  auth2PartnershipContent: "Partenaire de contenu",
  auth2RegisterAffiliateAdmin:
    "S'inscrire en tant qu'administrateur partenaire affilié",
  auth2AffiliateRoleHeading: "Votre rôle d'administrateur partenaire affilié :",
  auth2AffiliateRoleServices: "Gérer les services et produits partenaires",
  auth2AffiliateRoleAcquisition:
    "Acquisition de clients et gestion des partenariats",
  auth2AffiliateRoleAnalytics: "Analyses et suivi des performances",
  auth2AffiliateRoleBilling: "Facturation et commissions",
  auth2AffiliateRoleIntegration: "Intégration dans l'écosystème HealthPass",
  auth2OtherProviderType: "Autre type de prestataire ?",
  auth2ProviderSelection: "Choisir le type de prestataire",

  auth2PlanFree: "Formule gratuite",
  auth2PlanStandard: "Formule Standard",
  auth2PlanPremium: "Formule Premium",
  auth2PlanNoTimeLimit: "Sans limite de durée",
  auth2PlanMonthlyOrOneTime: "par mois (12 mois) ou {oneTime} en une fois",
  auth2FeatPersonalProfile: "Profil personnel",
  auth2FeatStandardQuestionnaire: "Questionnaire médical standard",
  auth2FeatQrSharing: "Partage des données par QR code",
  auth2FeatStorage6Months: "Conservation des données pendant 6 mois",
  auth2FeatEmergencyContacts: "Contacts d'urgence",
  auth2FeatAllFree: "Toutes les fonctions de la formule gratuite",
  auth2FeatMedMessage: "MedMessage : notifications intelligentes",
  auth2FeatVaccinationsMedPlan:
    "Certificats de vaccination et plan de médication",
  auth2FeatTravelReadiness: "Bilan de préparation au voyage",
  auth2FeatPartnerOffers: "Offres de voyage partenaires",
  auth2FeatConsentManagement: "Gestion des consentements",
  auth2FeatStorage100Mb: "Jusqu'à 100 Mo de stockage",
  auth2FeatAllStandard: "Toutes les fonctions Standard",
  auth2FeatTinaAssistant: "Tina, l'assistante IA de santé en voyage",
  auth2FeatCareProfile: "Profil de soins et profil nutritionnel",
  auth2FeatHealthMonitoring: "Suivi de santé pendant le voyage",
  auth2FeatInviteProvider: "Inviter un prestataire de santé",
  auth2FeatPersonalizedOffers: "Offres personnalisées",
  auth2WelcomeToHealthPass: "Bienvenue sur HealthPass",
  auth2WelcomeSubtitle:
    "Votre santé, toujours avec vous – sécurisée et numérique",
  auth2DateOfBirthOptional: "Date de naissance (facultatif)",
  auth2PhoneOptional: "Numéro de téléphone (facultatif)",
  auth2SecureAccessSubtitle: "Un accès sécurisé à vos données de santé",
  auth2PasswordMin8Label: "Mot de passe * (au moins 8 caractères)",
  auth2ChooseYourPlan: "Choisissez votre formule",
  auth2ChooseYourPlanSubtitle: "Adaptée à chaque type de voyageur",
  auth2Popular: "Populaire",
  auth2MoreFeatures: "+ {count} autres fonctionnalités",
  auth2AlmostDone: "Presque terminé !",
  auth2ConfirmRegistration: "Confirmez votre inscription",
  auth2YourSelection: "Votre sélection",
  auth2Plan: "Formule",
  auth2IAcceptThe: "J'accepte les",
  auth2And: "et la",
  auth2NewsletterOptIn:
    "Je souhaite recevoir la newsletter avec des conseils santé pour voyager (facultatif)",
  auth2CompleteRegistration: "Finaliser l'inscription",
  auth2SignInHere: "Connectez-vous ici",
};

const es: AppPagesAuthStrings = {
  auth2LogoAlt: "Logotipo de HealthPass",
  auth2Imprint: "Aviso legal",
  auth2EmailPlaceholder: "su-correo@ejemplo.com",
  auth2PersonalEmailPlaceholder: "juan@ejemplo.com",
  auth2FirstNamePlaceholder: "Juan",
  auth2LastNamePlaceholder: "García",
  auth2PhonePlaceholder: "+34 912 345 678",
  auth2AddressPlaceholder: "Calle Mayor 12, 28013 Madrid",
  auth2ShowPassword: "Mostrar contraseña",
  auth2HidePassword: "Ocultar contraseña",
  auth2PasswordMinPlaceholder: "Al menos 8 caracteres",
  auth2RepeatPasswordPlaceholder: "Repita la contraseña",
  auth2PasswordsDoNotMatch: "Las contraseñas no coinciden",
  auth2Registering: "Registrando…",
  auth2AlreadyHaveAccountSignIn: "¿Ya tiene una cuenta? Inicie sesión",
  auth2AlreadyRegisteredSignIn: "¿Ya está registrado? Inicie sesión",
  auth2GoToSignIn: "Ir al inicio de sesión",
  auth2Website: "Sitio web",
  auth2BusinessLicense: "Licencia comercial",
  auth2CompanyInformation: "Datos de la empresa",
  auth2CompanyNameRequired: "Nombre de la empresa *",
  auth2HealthcareProvider: "Proveedor sanitario",
  auth2TourOperator: "Operador turístico",
  auth2AffiliatePartner: "Socio afiliado",
  auth2BackToProviderSelection: "← Volver a la selección de proveedor",
  auth2CopyrightLine: "© {year} HealthPass. Todos los derechos reservados.",

  auth2SecondStepFailed:
    "No hemos podido iniciar el segundo paso. Inténtelo de nuevo en breve.",
  auth2PasskeyFailed:
    "No se ha podido usar esa llave de acceso para iniciar sesión.",
  auth2SigningIn: "Iniciando sesión…",
  auth2Or: "o",
  auth2WaitingForPasskey: "Esperando su llave de acceso…",
  auth2SignInWithPasskey: "Iniciar sesión con una llave de acceso",

  auth2ResetEmailError: "No hemos podido enviar el correo. Inténtelo de nuevo.",
  auth2ResetLinkSentTo:
    "Hemos enviado un enlace para restablecer la contraseña a",
  auth2NoEmailReceived: "¿No ha recibido el correo? Revise su carpeta de spam.",
  auth2ResendEmail: "Reenviar correo",
  auth2ResetLinkInfo:
    "Recibirá un correo con un enlace seguro para restablecer su contraseña.",

  auth2ResetExpired:
    "Ese enlace ha caducado. Los enlaces de restablecimiento duran una hora y solo funcionan una vez: solicite uno nuevo.",
  auth2ResetInvalid:
    "Ese enlace no es válido. Puede que ya se haya usado o que su programa de correo haya cortado la dirección.",
  auth2ResetUnavailable:
    "Algo en nuestro sistema no está disponible temporalmente. Su enlace sigue siendo válido: inténtelo de nuevo en unos minutos.",
  auth2PasswordsNotSame: "Las dos contraseñas no son iguales.",
  auth2ChooseStrongerPassword: "Elija una contraseña más segura.",
  auth2ChooseNewPassword: "Elija una nueva contraseña",
  auth2PasswordChanged: "Contraseña cambiada",
  auth2SignInWithNewPassword:
    "Ya puede iniciar sesión con su nueva contraseña.",
  auth2CouldNotReset: "No hemos podido restablecerla",
  auth2RequestNewLink: "Solicitar un nuevo enlace",
  auth2PasswordRules:
    "Al menos 8 caracteres, con una mayúscula, una minúscula, un número y un símbolo.",
  auth2RepeatNewPassword: "Repítala",
  auth2SetNewPassword: "Guardar nueva contraseña",
  auth2NeedsEmailLink: "Esta página necesita el enlace de su correo.",

  auth2ConfirmYourEmail: "Confirme su correo electrónico",
  auth2ConfirmingEmail: "Confirmando su correo electrónico",
  auth2OneMoment: "Un momento.",
  auth2EmailConfirmedTitle: "Correo electrónico confirmado",
  auth2EmailConfirmedBody:
    "Su dirección está verificada. Ya puede iniciar sesión y usar HealthPass.",
  auth2VerifyExpiredTitle: "Ese enlace ha caducado",
  auth2VerifyExpiredBody:
    "Los enlaces de verificación duran 24 horas y solo funcionan una vez. Inicie sesión y solicite uno nuevo en los ajustes de su cuenta.",
  auth2VerifyInvalidTitle: "Ese enlace no es válido",
  auth2VerifyInvalidBody:
    "Puede que ya se haya usado o que su programa de correo haya cortado la dirección. Intente abrirlo de nuevo desde el mensaje original.",
  auth2VerifyUnavailableTitle: "No hemos podido confirmarlo ahora mismo",
  auth2VerifyUnavailableBody:
    "Algo en nuestro sistema no está disponible temporalmente. Su enlace sigue siendo válido: inténtelo de nuevo en unos minutos.",

  auth2ProviderRegistration: "Registro de proveedores",
  auth2ProviderRegistrationSubtitle:
    "Elija su tipo de proveedor para registrarse",
  auth2HealthcareProviderDesc:
    "Clínicas, consultas médicas, centros de vacunación y otros centros sanitarios",
  auth2TagVaccinationCertificates: "Certificados de vacunación",
  auth2TagTravelMedicine: "Medicina del viajero",
  auth2TagHealthRecords: "Historiales de salud",
  auth2TourOperatorDesc:
    "Operadores turísticos, agencias de viajes y socios del sector turístico",
  auth2TagTripPlanning: "Planificación de viajes",
  auth2TagCustomerSupport: "Atención al cliente",
  auth2TagHealthRequirements: "Requisitos sanitarios",
  auth2AffiliatePartnerDesc:
    "Aseguradoras, socios tecnológicos y proveedores de servicios",
  auth2TagPartnerships: "Colaboraciones",
  auth2TagServices: "Servicios",
  auth2PrivateUserQuestion: "¿Se registra a título personal?",
  auth2StandardRegistration: "Crear una cuenta personal",

  auth2TourOperatorAdminTitle: "Administrador de operador turístico",
  auth2TourOperatorAdminSubtitle:
    "Regístrese como administrador de un operador turístico",
  auth2TravelEmailPlaceholder: "juan@agenciadeviajes.es",
  auth2TravelCompanyPlaceholder: "Viajes Ejemplo S.L.",
  auth2OperatorType: "Tipo de operador",
  auth2OpTravelAgency: "Agencia de viajes",
  auth2OpCruiseLine: "Naviera de cruceros",
  auth2OpAirline: "Aerolínea",
  auth2OpHotelChain: "Cadena hotelera",
  auth2OpCarRental: "Alquiler de coches",
  auth2OpAdventureTours: "Viajes de aventura",
  auth2OpCulturalTours: "Viajes culturales",
  auth2OpBusinessTravel: "Viajes de negocios",
  auth2TourOperatorPermissions:
    "Permisos del administrador de operador turístico:",
  auth2TourPermTrips: "Gestionar viajes y reservas",
  auth2TourPermHealthRequirements:
    "Consultar los requisitos sanitarios de los destinos",
  auth2TourPermStaff: "Invitar y gestionar al personal",
  auth2TourPermSupport: "Atención al cliente",
  auth2TourPermReports: "Ver informes y estadísticas",
  auth2RegisterTourOperatorAdmin:
    "Registrarse como administrador de operador turístico",

  auth2CreateAdminAccount: "Crear una cuenta de administrador",
  auth2OrganizationRequired: "Organización / institución *",
  auth2OrganizationPlaceholder: "p. ej., Centro Médico Municipal",
  auth2ProviderAdminCan: "Como administrador de un proveedor sanitario, puede:",
  auth2ProviderPermHealthData: "Ver y gestionar datos de salud",
  auth2ProviderPermInviteStaff: "Invitar a más personal sanitario",
  auth2ProviderPermCertificates: "Emitir certificados de vacunación",
  auth2ProviderPermConsultations: "Ofrecer consultas de medicina del viajero",
  auth2RegisterAsRegularUser: "Registrarse como usuario normal",

  auth2HcpRegistrationTitle: "Registro de proveedores sanitarios",
  auth2HcpRegistrationSubtitle: "Registre su centro sanitario en HealthPass",
  auth2FacilityInformation: "Datos del centro",
  auth2FacilityNameRequired: "Nombre del centro *",
  auth2FacilityTypeRequired: "Tipo de centro *",
  auth2FacilityHospital: "Hospital",
  auth2FacilityClinic: "Clínica",
  auth2FacilityPharmacy: "Farmacia",
  auth2FacilityLaboratory: "Laboratorio",
  auth2FacilityDoctorOffice: "Consulta médica",
  auth2FacilityNursingHome: "Residencia de mayores",
  auth2FacilityRehabCenter: "Centro de rehabilitación",
  auth2FacilityMentalHealth: "Centro de salud mental",
  auth2FacilityEmergencyServices: "Servicios de urgencias",
  auth2FacilityEmailRequired: "Correo electrónico del centro *",
  auth2LicenseNumber: "Número de licencia / autorización",
  auth2StreetAddressRequired: "Calle y número *",
  auth2ServicesAndSpecialties: "Servicios y especialidades",
  auth2ServicesCommaSeparated: "Servicios (separados por comas)",
  auth2ServicesPlaceholder: "p. ej., Medicina general, Cirugía, Radiología",
  auth2SpecialtiesCommaSeparated: "Especialidades (separadas por comas)",
  auth2SpecialtiesPlaceholder: "p. ej., Cardiología, Neurología, Pediatría",
  auth2AdministratorAccount: "Cuenta de administrador",

  auth2AffiliateRegistered:
    "¡Administrador de socio afiliado registrado correctamente!",
  auth2AffiliatePartnerAdmin: "Administrador de socio afiliado",
  auth2AffiliateSubtitle: "Registro para administradores de socios afiliados",
  auth2PartnerEmailPlaceholder: "admin@empresasocia.es",
  auth2InsuranceCompanyPlaceholder: "Seguros Ejemplo S.A.",
  auth2WebsitePlaceholder: "https://www.empresasocia.es",
  auth2CompanyDescriptionPlaceholder:
    "Breve descripción de su empresa y de los servicios que ofrece…",
  auth2PartnerType: "Tipo de socio",
  auth2PartnerTravelInsurance: "Seguro de viaje",
  auth2PartnerHealthInsurance: "Seguro de salud",
  auth2PartnerMedicalEquipment: "Equipamiento médico",
  auth2PartnerPharmaceutical: "Farmacéutica",
  auth2PartnerWellness: "Servicios de bienestar",
  auth2PartnerTelemedicine: "Telemedicina",
  auth2PartnerDiagnostics: "Servicios de diagnóstico",
  auth2PartnerEmergencyAssistance: "Asistencia en emergencias",
  auth2PartnerLogistics: "Proveedor logístico",
  auth2PartnerFintech: "FinTech",
  auth2PartnershipType: "Tipo de colaboración",
  auth2PartnershipReferral: "Recomendación",
  auth2PartnershipCommission: "Basada en comisiones",
  auth2PartnershipRevenueShare: "Reparto de ingresos",
  auth2PartnershipWhiteLabel: "Marca blanca",
  auth2PartnershipReseller: "Revendedor",
  auth2PartnershipTechnology: "Socio tecnológico",
  auth2PartnershipContent: "Socio de contenidos",
  auth2RegisterAffiliateAdmin:
    "Registrarse como administrador de socio afiliado",
  auth2AffiliateRoleHeading: "Su función como administrador de socio afiliado:",
  auth2AffiliateRoleServices: "Gestionar los servicios y productos del socio",
  auth2AffiliateRoleAcquisition:
    "Captación de clientes y gestión de colaboraciones",
  auth2AffiliateRoleAnalytics: "Análisis y seguimiento del rendimiento",
  auth2AffiliateRoleBilling: "Facturación y comisiones",
  auth2AffiliateRoleIntegration: "Integración en el ecosistema de HealthPass",
  auth2OtherProviderType: "¿Otro tipo de proveedor?",
  auth2ProviderSelection: "Elegir tipo de proveedor",

  auth2PlanFree: "Plan gratuito",
  auth2PlanStandard: "Plan Standard",
  auth2PlanPremium: "Plan Premium",
  auth2PlanNoTimeLimit: "Sin límite de tiempo",
  auth2PlanMonthlyOrOneTime: "al mes (12 meses) o {oneTime} en un pago único",
  auth2FeatPersonalProfile: "Perfil personal",
  auth2FeatStandardQuestionnaire: "Cuestionario estándar de historia clínica",
  auth2FeatQrSharing: "Compartir datos mediante código QR",
  auth2FeatStorage6Months: "Almacenamiento de datos durante 6 meses",
  auth2FeatEmergencyContacts: "Contactos de emergencia",
  auth2FeatAllFree: "Todo lo del plan gratuito",
  auth2FeatMedMessage: "MedMessage: notificaciones inteligentes",
  auth2FeatVaccinationsMedPlan:
    "Certificados de vacunación y plan de medicación",
  auth2FeatTravelReadiness: "Comprobación de preparación para el viaje",
  auth2FeatPartnerOffers: "Ofertas de viaje de socios",
  auth2FeatConsentManagement: "Gestión de consentimientos",
  auth2FeatStorage100Mb: "Hasta 100 MB de almacenamiento",
  auth2FeatAllStandard: "Todo lo del plan Standard",
  auth2FeatTinaAssistant: "Tina, la asistente de IA para la salud en viajes",
  auth2FeatCareProfile: "Perfil de cuidados y perfil nutricional",
  auth2FeatHealthMonitoring: "Seguimiento de la salud durante el viaje",
  auth2FeatInviteProvider: "Invitar a un proveedor sanitario",
  auth2FeatPersonalizedOffers: "Ofertas personalizadas",
  auth2WelcomeToHealthPass: "Bienvenido a HealthPass",
  auth2WelcomeSubtitle: "Su salud, siempre con usted: segura y digital",
  auth2DateOfBirthOptional: "Fecha de nacimiento (opcional)",
  auth2PhoneOptional: "Número de teléfono (opcional)",
  auth2SecureAccessSubtitle: "Acceso seguro a sus datos de salud",
  auth2PasswordMin8Label: "Contraseña * (al menos 8 caracteres)",
  auth2ChooseYourPlan: "Elija su plan",
  auth2ChooseYourPlanSubtitle: "Ideal para cada tipo de viajero",
  auth2Popular: "Popular",
  auth2MoreFeatures: "+ {count} funciones más",
  auth2AlmostDone: "¡Casi listo!",
  auth2ConfirmRegistration: "Confirme su registro",
  auth2YourSelection: "Su selección",
  auth2Plan: "Plan",
  auth2IAcceptThe: "Acepto los",
  auth2And: "y la",
  auth2NewsletterOptIn:
    "Quiero recibir el boletín con consejos de salud para viajar (opcional)",
  auth2CompleteRegistration: "Completar registro",
  auth2SignInHere: "Inicie sesión aquí",
};

const pt: AppPagesAuthStrings = {
  auth2LogoAlt: "Logótipo HealthPass",
  auth2Imprint: "Informação legal",
  auth2EmailPlaceholder: "o-seu-email@exemplo.pt",
  auth2PersonalEmailPlaceholder: "joao@exemplo.pt",
  auth2FirstNamePlaceholder: "João",
  auth2LastNamePlaceholder: "Silva",
  auth2PhonePlaceholder: "+351 912 345 678",
  auth2AddressPlaceholder: "Rua Augusta 100, 1100-053 Lisboa",
  auth2ShowPassword: "Mostrar palavra-passe",
  auth2HidePassword: "Ocultar palavra-passe",
  auth2PasswordMinPlaceholder: "Pelo menos 8 caracteres",
  auth2RepeatPasswordPlaceholder: "Repita a palavra-passe",
  auth2PasswordsDoNotMatch: "As palavras-passe não coincidem",
  auth2Registering: "A registar…",
  auth2AlreadyHaveAccountSignIn: "Já tem conta? Inicie sessão",
  auth2AlreadyRegisteredSignIn: "Já está registado? Inicie sessão",
  auth2GoToSignIn: "Ir para o início de sessão",
  auth2Website: "Website",
  auth2BusinessLicense: "Licença comercial",
  auth2CompanyInformation: "Dados da empresa",
  auth2CompanyNameRequired: "Nome da empresa *",
  auth2HealthcareProvider: "Prestador de cuidados de saúde",
  auth2TourOperator: "Operador turístico",
  auth2AffiliatePartner: "Parceiro afiliado",
  auth2BackToProviderSelection: "← Voltar à seleção de prestador",
  auth2CopyrightLine: "© {year} HealthPass. Todos os direitos reservados.",

  auth2SecondStepFailed:
    "Não foi possível iniciar o segundo passo. Tente novamente dentro de momentos.",
  auth2PasskeyFailed:
    "Não foi possível utilizar essa chave de acesso para iniciar sessão.",
  auth2SigningIn: "A iniciar sessão…",
  auth2Or: "ou",
  auth2WaitingForPasskey: "A aguardar a sua chave de acesso…",
  auth2SignInWithPasskey: "Iniciar sessão com uma chave de acesso",

  auth2ResetEmailError: "Não foi possível enviar o email. Tente novamente.",
  auth2ResetLinkSentTo:
    "Enviámos uma ligação para redefinir a palavra-passe para",
  auth2NoEmailReceived: "Não recebeu o email? Verifique a pasta de spam.",
  auth2ResendEmail: "Reenviar email",
  auth2ResetLinkInfo:
    "Irá receber um email com uma ligação segura para redefinir a sua palavra-passe.",

  auth2ResetExpired:
    "Essa ligação expirou. As ligações de redefinição são válidas durante uma hora e apenas uma vez – peça uma nova.",
  auth2ResetInvalid:
    "Essa ligação não é válida. Pode já ter sido utilizada, ou o seu programa de email cortou o endereço.",
  auth2ResetUnavailable:
    "Algo do nosso lado está temporariamente indisponível. A sua ligação continua válida – tente novamente dentro de alguns minutos.",
  auth2PasswordsNotSame: "As duas palavras-passe não são iguais.",
  auth2ChooseStrongerPassword: "Escolha uma palavra-passe mais forte.",
  auth2ChooseNewPassword: "Escolha uma nova palavra-passe",
  auth2PasswordChanged: "Palavra-passe alterada",
  auth2SignInWithNewPassword:
    "Já pode iniciar sessão com a sua nova palavra-passe.",
  auth2CouldNotReset: "Não foi possível redefini-la",
  auth2RequestNewLink: "Pedir uma nova ligação",
  auth2PasswordRules:
    "Pelo menos 8 caracteres, com uma letra maiúscula, uma minúscula, um número e um símbolo.",
  auth2RepeatNewPassword: "Repita-a",
  auth2SetNewPassword: "Guardar nova palavra-passe",
  auth2NeedsEmailLink: "Esta página precisa da ligação enviada por email.",

  auth2ConfirmYourEmail: "Confirme o seu email",
  auth2ConfirmingEmail: "A confirmar o seu email",
  auth2OneMoment: "Um momento.",
  auth2EmailConfirmedTitle: "Email confirmado",
  auth2EmailConfirmedBody:
    "O seu endereço está verificado. Já pode iniciar sessão e utilizar o HealthPass.",
  auth2VerifyExpiredTitle: "Essa ligação expirou",
  auth2VerifyExpiredBody:
    "As ligações de verificação são válidas durante 24 horas e apenas uma vez. Inicie sessão e peça uma nova nas definições da sua conta.",
  auth2VerifyInvalidTitle: "Essa ligação não é válida",
  auth2VerifyInvalidBody:
    "Pode já ter sido utilizada, ou o seu programa de email cortou o endereço. Tente abri-la novamente a partir da mensagem original.",
  auth2VerifyUnavailableTitle: "Não foi possível confirmar neste momento",
  auth2VerifyUnavailableBody:
    "Algo do nosso lado está temporariamente indisponível. A sua ligação continua válida – tente novamente dentro de alguns minutos.",

  auth2ProviderRegistration: "Registo de prestadores",
  auth2ProviderRegistrationSubtitle:
    "Escolha o seu tipo de prestador para se registar",
  auth2HealthcareProviderDesc:
    "Clínicas, consultórios médicos, centros de vacinação e outras unidades de saúde",
  auth2TagVaccinationCertificates: "Certificados de vacinação",
  auth2TagTravelMedicine: "Medicina do viajante",
  auth2TagHealthRecords: "Registos de saúde",
  auth2TourOperatorDesc:
    "Operadores turísticos, agências de viagens e parceiros do turismo",
  auth2TagTripPlanning: "Planeamento de viagens",
  auth2TagCustomerSupport: "Apoio ao cliente",
  auth2TagHealthRequirements: "Requisitos sanitários",
  auth2AffiliatePartnerDesc:
    "Seguradoras, parceiros tecnológicos e prestadores de serviços",
  auth2TagPartnerships: "Parcerias",
  auth2TagServices: "Serviços",
  auth2PrivateUserQuestion: "Está a registar-se a título pessoal?",
  auth2StandardRegistration: "Criar uma conta pessoal",

  auth2TourOperatorAdminTitle: "Administrador de operador turístico",
  auth2TourOperatorAdminSubtitle:
    "Registe-se como administrador de um operador turístico",
  auth2TravelEmailPlaceholder: "joao@agenciadeviagens.pt",
  auth2TravelCompanyPlaceholder: "Viagens Exemplo, Lda.",
  auth2OperatorType: "Tipo de operador",
  auth2OpTravelAgency: "Agência de viagens",
  auth2OpCruiseLine: "Companhia de cruzeiros",
  auth2OpAirline: "Companhia aérea",
  auth2OpHotelChain: "Cadeia hoteleira",
  auth2OpCarRental: "Aluguer de automóveis",
  auth2OpAdventureTours: "Viagens de aventura",
  auth2OpCulturalTours: "Viagens culturais",
  auth2OpBusinessTravel: "Viagens de negócios",
  auth2TourOperatorPermissions:
    "Permissões do administrador de operador turístico:",
  auth2TourPermTrips: "Gerir viagens e reservas",
  auth2TourPermHealthRequirements:
    "Consultar os requisitos sanitários dos destinos",
  auth2TourPermStaff: "Convidar e gerir colaboradores",
  auth2TourPermSupport: "Apoio ao cliente",
  auth2TourPermReports: "Ver relatórios e estatísticas",
  auth2RegisterTourOperatorAdmin:
    "Registar-se como administrador de operador turístico",

  auth2CreateAdminAccount: "Criar uma conta de administrador",
  auth2OrganizationRequired: "Organização / instituição *",
  auth2OrganizationPlaceholder: "p. ex. Centro Médico Municipal",
  auth2ProviderAdminCan:
    "Como administrador de um prestador de cuidados de saúde, pode:",
  auth2ProviderPermHealthData: "Ver e gerir dados de saúde",
  auth2ProviderPermInviteStaff: "Convidar mais profissionais",
  auth2ProviderPermCertificates: "Emitir certificados de vacinação",
  auth2ProviderPermConsultations: "Oferecer consultas de medicina do viajante",
  auth2RegisterAsRegularUser: "Registar-se como utilizador comum",

  auth2HcpRegistrationTitle: "Registo de prestadores de cuidados de saúde",
  auth2HcpRegistrationSubtitle: "Registe a sua unidade de saúde no HealthPass",
  auth2FacilityInformation: "Dados da unidade",
  auth2FacilityNameRequired: "Nome da unidade *",
  auth2FacilityTypeRequired: "Tipo de unidade *",
  auth2FacilityHospital: "Hospital",
  auth2FacilityClinic: "Clínica",
  auth2FacilityPharmacy: "Farmácia",
  auth2FacilityLaboratory: "Laboratório",
  auth2FacilityDoctorOffice: "Consultório médico",
  auth2FacilityNursingHome: "Lar de idosos",
  auth2FacilityRehabCenter: "Centro de reabilitação",
  auth2FacilityMentalHealth: "Centro de saúde mental",
  auth2FacilityEmergencyServices: "Serviços de urgência",
  auth2FacilityEmailRequired: "Email da unidade *",
  auth2LicenseNumber: "Número de licença / autorização",
  auth2StreetAddressRequired: "Rua e número *",
  auth2ServicesAndSpecialties: "Serviços e especialidades",
  auth2ServicesCommaSeparated: "Serviços (separados por vírgulas)",
  auth2ServicesPlaceholder: "p. ex. Medicina geral, Cirurgia, Radiologia",
  auth2SpecialtiesCommaSeparated: "Especialidades (separadas por vírgulas)",
  auth2SpecialtiesPlaceholder: "p. ex. Cardiologia, Neurologia, Pediatria",
  auth2AdministratorAccount: "Conta de administrador",

  auth2AffiliateRegistered:
    "Administrador de parceiro afiliado registado com sucesso!",
  auth2AffiliatePartnerAdmin: "Administrador de parceiro afiliado",
  auth2AffiliateSubtitle: "Registo para administradores de parceiros afiliados",
  auth2PartnerEmailPlaceholder: "admin@empresaparceira.pt",
  auth2InsuranceCompanyPlaceholder: "Seguros Exemplo, S.A.",
  auth2WebsitePlaceholder: "https://www.empresaparceira.pt",
  auth2CompanyDescriptionPlaceholder:
    "Breve descrição da sua empresa e dos serviços que oferece…",
  auth2PartnerType: "Tipo de parceiro",
  auth2PartnerTravelInsurance: "Seguro de viagem",
  auth2PartnerHealthInsurance: "Seguro de saúde",
  auth2PartnerMedicalEquipment: "Equipamento médico",
  auth2PartnerPharmaceutical: "Farmacêutica",
  auth2PartnerWellness: "Serviços de bem-estar",
  auth2PartnerTelemedicine: "Telemedicina",
  auth2PartnerDiagnostics: "Serviços de diagnóstico",
  auth2PartnerEmergencyAssistance: "Assistência em emergências",
  auth2PartnerLogistics: "Prestador de logística",
  auth2PartnerFintech: "FinTech",
  auth2PartnershipType: "Tipo de parceria",
  auth2PartnershipReferral: "Recomendação",
  auth2PartnershipCommission: "Com base em comissões",
  auth2PartnershipRevenueShare: "Partilha de receitas",
  auth2PartnershipWhiteLabel: "Marca branca",
  auth2PartnershipReseller: "Revendedor",
  auth2PartnershipTechnology: "Parceiro tecnológico",
  auth2PartnershipContent: "Parceiro de conteúdos",
  auth2RegisterAffiliateAdmin:
    "Registar-se como administrador de parceiro afiliado",
  auth2AffiliateRoleHeading:
    "A sua função como administrador de parceiro afiliado:",
  auth2AffiliateRoleServices: "Gerir serviços e produtos do parceiro",
  auth2AffiliateRoleAcquisition: "Angariação de clientes e gestão de parcerias",
  auth2AffiliateRoleAnalytics: "Análises e acompanhamento do desempenho",
  auth2AffiliateRoleBilling: "Faturação e comissões",
  auth2AffiliateRoleIntegration: "Integração no ecossistema HealthPass",
  auth2OtherProviderType: "Outro tipo de prestador?",
  auth2ProviderSelection: "Escolher o tipo de prestador",

  auth2PlanFree: "Plano gratuito",
  auth2PlanStandard: "Plano Standard",
  auth2PlanPremium: "Plano Premium",
  auth2PlanNoTimeLimit: "Sem limite de tempo",
  auth2PlanMonthlyOrOneTime:
    "por mês (12 meses) ou {oneTime} num pagamento único",
  auth2FeatPersonalProfile: "Perfil pessoal",
  auth2FeatStandardQuestionnaire: "Questionário clínico padrão",
  auth2FeatQrSharing: "Partilha de dados por código QR",
  auth2FeatStorage6Months: "Armazenamento de dados durante 6 meses",
  auth2FeatEmergencyContacts: "Contactos de emergência",
  auth2FeatAllFree: "Tudo o que inclui o plano gratuito",
  auth2FeatMedMessage: "MedMessage: notificações inteligentes",
  auth2FeatVaccinationsMedPlan:
    "Certificados de vacinação e plano de medicação",
  auth2FeatTravelReadiness: "Verificação de preparação para a viagem",
  auth2FeatPartnerOffers: "Ofertas de viagem de parceiros",
  auth2FeatConsentManagement: "Gestão de consentimentos",
  auth2FeatStorage100Mb: "Até 100 MB de armazenamento",
  auth2FeatAllStandard: "Tudo o que inclui o plano Standard",
  auth2FeatTinaAssistant: "Tina, a assistente de IA para a saúde em viagem",
  auth2FeatCareProfile: "Perfil de cuidados e perfil nutricional",
  auth2FeatHealthMonitoring: "Monitorização da saúde em viagem",
  auth2FeatInviteProvider: "Convidar um prestador de cuidados de saúde",
  auth2FeatPersonalizedOffers: "Ofertas personalizadas",
  auth2WelcomeToHealthPass: "Bem-vindo ao HealthPass",
  auth2WelcomeSubtitle: "A sua saúde, sempre consigo – segura e digital",
  auth2DateOfBirthOptional: "Data de nascimento (opcional)",
  auth2PhoneOptional: "Número de telefone (opcional)",
  auth2SecureAccessSubtitle: "Acesso seguro aos seus dados de saúde",
  auth2PasswordMin8Label: "Palavra-passe * (pelo menos 8 caracteres)",
  auth2ChooseYourPlan: "Escolha o seu plano",
  auth2ChooseYourPlanSubtitle: "Ideal para todo o tipo de viajante",
  auth2Popular: "Popular",
  auth2MoreFeatures: "+ {count} funcionalidades adicionais",
  auth2AlmostDone: "Quase terminado!",
  auth2ConfirmRegistration: "Confirme o seu registo",
  auth2YourSelection: "A sua seleção",
  auth2Plan: "Plano",
  auth2IAcceptThe: "Aceito os",
  auth2And: "e a",
  auth2NewsletterOptIn:
    "Quero receber a newsletter com dicas de saúde para viajar (opcional)",
  auth2CompleteRegistration: "Concluir registo",
  auth2SignInHere: "Inicie sessão aqui",
};

export const appPagesAuthStrings: Record<Language, AppPagesAuthStrings> = {
  en,
  de,
  fr,
  es,
  pt,
};
